import { useCallback, useEffect, useMemo, useRef, useState, memo, startTransition } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import clsx from 'classnames';
import dayjs from 'dayjs';
import { CreditCard, Check, ChevronDown, X, Layers, Flame, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './SubscriptionPlans.module.scss';
import { PaymentsApi } from 'entries/apis/Payments.api';
import {
  filterCommercialBasicPlans,
  filterCommercialPremiumPlans,
  filterPlansByTier,
  findPlanByPriceId,
  formatEuroAmount,
  getPlanBillingLabel,
  buildTierComparisonRows,
  getTierCardHighlights,
  getTierLabel,
  getPlanTierLabel,
  isCustomPlanMode,
  isPremiumPriceId,
  buildIntervalOptions,
  isPlanSelectable,
  isUpgradePlan,
  parsePlanDisplay,
  resolvePremiumPriceIds,
  resolveSelectableIntervalType,
  sortPlansByInterval,
} from '../../utils/subscriptionUtils';
import {
  isPremiumPlan,
  resolvePlanIdFromSubscription,
} from 'Containers/Scheduler/containers/CalendarHeader/utils/getActivePlan';

const paymentsApi = PaymentsApi.getInstance();

const IntervalPicker = memo(({
  options,
  selectedIntervalType,
  onSelectInterval,
  disabled,
  isPremium,
  currentPriceId,
  label,
  activeTagLabel,
}) => {
  if (!options?.length || options.length <= 1) return null;

  return (
    <div className={styles.interval_picker}>
      {label ? <p className={styles.interval_picker_label}>{label}</p> : null}
      <div className={styles.interval_picker_buttons}>
        {options.map((option) => {
          const isSelected = selectedIntervalType === option.intervalType;
          const isActive = currentPriceId && option.planId === currentPriceId;

          return (
            <button
              key={option.planId}
              type='button'
              className={clsx(styles.interval_button, {
                [styles.interval_button_selected]: isSelected,
                [styles.interval_button_premium]: isPremium && isSelected,
                [styles.interval_button_active]: isActive,
                [styles.interval_button_has_savings]: option.savingsPercent != null,
              })}
              disabled={disabled}
              onClick={() => onSelectInterval?.(option.intervalType)}
              aria-pressed={isSelected}
              aria-current={isActive ? 'true' : undefined}
            >
              {isActive && (
                <span className={styles.interval_button_active_tag}>{activeTagLabel}</span>
              )}
              {option.savingsPercent != null && (
                <span className={styles.interval_button_savings_edge}>-{option.savingsPercent}%</span>
              )}
              <span className={styles.interval_button_label}>{option.intervalLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

IntervalPicker.displayName = 'IntervalPicker';

export const PlanTierCard = memo(({
  badge,
  tierLabel,
  pitch,
  plan,
  featureRows = [],
  featuresTitle,
  extrasTitle,
  excludedTitle,
  upgradeTagLabel,
  additionTagLabel,
  isPremium,
  isCurrent,
  isCustomPlan,
  isFeatured,
  intervalOptions = [],
  defaultIntervalType,
  currentPriceId,
  disabled,
  isSecondary,
  isSubmitting,
  ctaLabel,
  onPlanAction,
  billingLabel,
  activeTagLabel,
  currentPlanCtaLabel,
  confirmIntervalLabel,
}) => {
  const [selectedIntervalType, setSelectedIntervalType] = useState(defaultIntervalType);

  useEffect(() => {
    setSelectedIntervalType(defaultIntervalType);
  }, [defaultIntervalType]);

  const handleSelectInterval = useCallback((intervalType) => {
    startTransition(() => setSelectedIntervalType(intervalType));
  }, []);

  const selectedOption = useMemo(
    () => intervalOptions.find((option) => option.intervalType === selectedIntervalType) || null,
    [intervalOptions, selectedIntervalType],
  );
  const displayPlan = selectedOption?.display ?? plan;
  const fromPrice = useMemo(() => {
    if (!intervalOptions.length) return null;
    return Math.min(...intervalOptions.map((option) => option.display.price));
  }, [intervalOptions]);
  const isActivePlan = Boolean(currentPriceId && displayPlan?.id === currentPriceId);
  const isPreviewSelection = Boolean(isCurrent && displayPlan && currentPriceId && !isActivePlan);
  const showPeriodPrimary = Boolean(displayPlan?.billingMonths > 1);
  const includedFeatures = useMemo(
    () => (featureRows || []).filter(
      (feature) => feature.included && !feature.isUpgrade && !feature.isAddition,
    ),
    [featureRows],
  );
  const upgradeFeatures = useMemo(
    () => {
      const extras = (featureRows || []).filter(
        (feature) => feature.included && (feature.isUpgrade || feature.isAddition),
      );
      return [
        ...extras.filter((feature) => feature.isAddition),
        ...extras.filter((feature) => feature.isUpgrade),
      ];
    },
    [featureRows],
  );
  const excludedFeatures = useMemo(
    () => (featureRows || []).filter((feature) => !feature.included),
    [featureRows],
  );

  const renderFeatureList = (rows) => (
    <ul className={styles.tier_card_features}>
      {rows.map((feature) => (
        <li
          key={feature.label}
          className={clsx({
            [styles.tier_card_feature_excluded]: !feature.included,
            [styles.tier_card_feature_upgrade]: feature.isUpgrade,
            [styles.tier_card_feature_addition]: feature.isAddition,
          })}
        >
          <span
            className={clsx(styles.tier_card_feature_icon, {
              [styles.tier_card_feature_icon_excluded]: !feature.included,
              [styles.tier_card_feature_icon_upgrade]: feature.isUpgrade,
              [styles.tier_card_feature_icon_addition]: feature.isAddition,
            })}
            aria-hidden
          >
            {!feature.included ? (
              <X size={14} strokeWidth={2.8} />
            ) : feature.isUpgrade || feature.isAddition ? (
              <Plus size={16} strokeWidth={2.8} />
            ) : (
              <Check size={16} strokeWidth={2.6} />
            )}
          </span>
          <span className={styles.tier_card_feature_label}>
            <span>{feature.label}</span>
            {feature.isUpgrade && upgradeTagLabel ? (
              <span className={styles.tier_card_feature_upgrade_badge}>{upgradeTagLabel}</span>
            ) : null}
            {feature.isAddition && additionTagLabel ? (
              <span className={styles.tier_card_feature_addition_badge}>{additionTagLabel}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );

  const handleFooterClick = () => {
    if (!selectedOption?.rawPlan || disabled) return;
    onPlanAction?.(selectedOption.rawPlan);
  };

  const renderFooterAction = () => {
    if (isCustomPlan && isCurrent) {
      return (
        <button
          type='button'
          className={clsx(styles.tier_card_cta, styles.tier_card_cta_native, styles.tier_card_cta_active)}
          disabled
        >
          {currentPlanCtaLabel}
        </button>
      );
    }

    if (isActivePlan && isCurrent) {
      return (
        <button
          type='button'
          className={clsx(styles.tier_card_cta, styles.tier_card_cta_native, styles.tier_card_cta_active)}
          disabled
        >
          {currentPlanCtaLabel}
        </button>
      );
    }

    if (isPreviewSelection) {
      return (
        <button
          type='button'
          className={clsx(styles.tier_card_cta, styles.tier_card_cta_native)}
          disabled={disabled}
          onClick={handleFooterClick}
        >
          {confirmIntervalLabel?.(displayPlan.intervalLabel) ||
            `Conferma passaggio a ${displayPlan.intervalLabel}`}
        </button>
      );
    }

    if (ctaLabel) {
      return (
        <button
          type='button'
          className={clsx(styles.tier_card_cta, styles.tier_card_cta_native, {
            [styles.tier_card_cta_premium]: isPremium,
          })}
          disabled={disabled || !selectedOption}
          onClick={handleFooterClick}
        >
          {isSubmitting ? <CircularProgress size={20} color='inherit' /> : ctaLabel}
        </button>
      );
    }

    return null;
  };

  return (
    <div
      className={clsx(styles.tier_card_shell, {
        [styles.tier_card_shell_featured]: isFeatured,
      })}
    >
      {badge && (
        <span
          className={clsx(styles.tier_card_edge_badge, {
            [styles.tier_card_edge_badge_current]: isCurrent,
            [styles.tier_card_edge_badge_premium]: isPremium && !isCurrent && !isFeatured,
            [styles.tier_card_edge_badge_featured]: isFeatured,
          })}
        >
          {badge}
        </span>
      )}
      <div
        className={clsx(styles.tier_card, {
          [styles.tier_card_current]: isCurrent,
          [styles.tier_card_premium]: isPremium && !isCurrent,
          [styles.tier_card_featured]: isFeatured,
          [styles.tier_card_active_plan]: isActivePlan,
          [styles.tier_card_preview_plan]: isPreviewSelection,
        })}
      >
        <div
          className={clsx(styles.tier_card_title, {
            [styles.tier_card_title_premium]: isPremium,
          })}
        >
          {isPremium ? (
            <Flame
              size={28}
              strokeWidth={2.1}
              fill="currentColor"
              className={clsx(styles.tier_card_title_icon, styles.tier_card_title_icon_flame)}
              aria-hidden
            />
          ) : (
            <Layers size={28} strokeWidth={2.1} className={styles.tier_card_title_icon} aria-hidden />
          )}
          <span>{tierLabel}</span>
        </div>

        {pitch ? <p className={styles.tier_card_pitch}>{pitch}</p> : null}

        {displayPlan ? (
          <div className={styles.tier_card_pricing}>
            <div
              className={clsx(styles.tier_card_price, {
                [styles.tier_card_price_period]: showPeriodPrimary,
              })}
            >
              {showPeriodPrimary ? (
                <>
                  <span className={styles.tier_card_price_main}>
                    <span className={styles.tier_card_price_amount}>
                      {formatEuroAmount(displayPlan.totalAmount)}€
                    </span>
                    <span className={styles.tier_card_price_period_label}>
                      {getPlanBillingLabel(displayPlan)}
                    </span>
                  </span>
                  <span className={styles.tier_card_price_secondary}>
                    {formatEuroAmount(displayPlan.price)}€ + IVA / mese
                  </span>
                </>
              ) : (
                <span className={styles.tier_card_price_main}>
                  <span className={styles.tier_card_price_amount}>
                    {formatEuroAmount(displayPlan.price)}€
                  </span>
                  <span className={styles.tier_card_price_period_label}>
                    {getPlanBillingLabel(displayPlan)}
                  </span>
                </span>
              )}
            </div>
            {displayPlan.discount && (
              <span className={styles.tier_card_savings_badge}>{displayPlan.discount}</span>
            )}
          </div>
        ) : fromPrice != null ? (
          <div className={styles.tier_card_price}>
            da {formatEuroAmount(fromPrice)}€ <span>+ IVA / mese</span>
          </div>
        ) : null}

        {intervalOptions.length > 1 && (
          <IntervalPicker
            options={intervalOptions}
            selectedIntervalType={selectedIntervalType}
            onSelectInterval={handleSelectInterval}
            disabled={disabled}
            isPremium={isPremium}
            currentPriceId={currentPriceId}
            label={billingLabel}
            activeTagLabel={activeTagLabel}
          />
        )}

        {includedFeatures.length > 0 || featuresTitle ? (
          <div className={styles.tier_card_highlights}>
            {featuresTitle ? (
              <p className={styles.tier_card_highlights_title}>{featuresTitle}</p>
            ) : null}
            {includedFeatures.length > 0 ? renderFeatureList(includedFeatures) : null}
          </div>
        ) : null}

        {upgradeFeatures.length > 0 ? (
          <div
            className={clsx(styles.tier_card_highlights, styles.tier_card_highlights_premium)}
          >
            {extrasTitle ? (
              <p className={styles.tier_card_highlights_title}>{extrasTitle}</p>
            ) : null}
            {renderFeatureList(upgradeFeatures)}
          </div>
        ) : null}

        {excludedFeatures.length > 0 ? (
          <div className={clsx(styles.tier_card_highlights, styles.tier_card_highlights_excluded)}>
            {excludedTitle ? (
              <p className={styles.tier_card_highlights_title}>{excludedTitle}</p>
            ) : null}
            {renderFeatureList(excludedFeatures)}
          </div>
        ) : null}

        <div className={styles.tier_card_footer}>{renderFooterAction()}</div>
      </div>
    </div>
  );
});

PlanTierCard.displayName = 'PlanTierCard';

export const SubscriptionPlans = ({
  plans = [],
  subscriptionStatus,
  products,
  clientId,
  billingSetupRequired = false,
  collectPaymentOnConfirm = false,
  isLoading,
  isSubmitting,
  onSelectPlan,
  errorMessage,
  onRetry,
  onFocusPayment,
}) => {
  const { t } = useTranslation();
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  // Utente abbonato: i piani restano chiusi finché non chiede "Cambia piano"
  const [plansExpanded, setPlansExpanded] = useState(false);
  const plansWrapRef = useRef(null);

  const currentPriceId = subscriptionStatus?.currentPriceId;
  const isSubscribed = products?.isSubscribed;
  const isCustomPlan = isCustomPlanMode(products?.planMode);
  const unpaidInvoice = products?.hasUnpaidInvoice;
  // RID in elaborazione: piano bloccato ma senza toni d'allarme (nessuna azione richiesta)
  const sepaProcessing = !!products?.sepaProcessing;
  const actionsDisabled = isSubmitting || unpaidInvoice || billingSetupRequired;
  const daysLeft = Number(products?.giorniRimanenti);
  const hasAccessDays = Number.isFinite(daysLeft) && daysLeft > 0;

  const currentRawPlan = useMemo(
    () => findPlanByPriceId(plans, currentPriceId),
    [plans, currentPriceId],
  );

  const premiumPriceIds = useMemo(
    () => resolvePremiumPriceIds(plans, products?.prezziPremium),
    [plans, products?.prezziPremium],
  );

  const currentPlan = useMemo(() => {
    if (!currentRawPlan) return null;
    const display = parsePlanDisplay(currentRawPlan);
    // Allinea etichetta a navbar (metadata Stripe a volte senza tier=premium)
    const resolvedId = resolvePlanIdFromSubscription({
      products,
      currentPriceId,
    });
    const isPremiumResolved =
      display.isPremium ||
      isPremiumPlan(resolvedId) ||
      (currentPriceId != null && premiumPriceIds.includes(String(currentPriceId)));

    return isPremiumResolved === display.isPremium
      ? display
      : { ...display, isPremium: isPremiumResolved };
  }, [currentRawPlan, products, currentPriceId, premiumPriceIds]);

  const onPremiumStripe = Boolean(
    currentPlan?.isPremium ||
      isPremiumPriceId(currentPriceId) ||
      (currentPriceId != null && premiumPriceIds.includes(String(currentPriceId))),
  );

  // Scaduto ma con sub Stripe: niente scelta piani (si paga la fattura / recovery).
  const hasStripeSubscription = Boolean(isSubscribed || currentPriceId);
  const expiredWithStripeSub = !hasAccessDays && hasStripeSubscription;

  // Scegliere Basic/Premium solo se non c'è una sub Stripe da rinnovare:
  // - nuovo / senza sub
  // - scaduto SENZA sub Stripe
  // - limbo prova Premium (prezzo > 30) ancora in accesso, senza Premium su Stripe
  const triedPremiumPricing = Number(products?.prezzo) > 30;
  const needsPlanChoice =
    !billingSetupRequired &&
    !unpaidInvoice &&
    !expiredWithStripeSub &&
    ((!isSubscribed && !currentPriceId) ||
      (!hasAccessDays && !hasStripeSubscription) ||
      (triedPremiumPricing && !onPremiumStripe && hasAccessDays && daysLeft <= 3));

  const basePlans = useMemo(
    () => sortPlansByInterval(filterPlansByTier(plans, false, premiumPriceIds)),
    [plans, premiumPriceIds],
  );
  const premiumPlans = useMemo(
    () => sortPlansByInterval(filterPlansByTier(plans, true, premiumPriceIds)),
    [plans, premiumPriceIds],
  );

  // Per la scelta piano usiamo solo il listino commerciale (non i price 50€ storici).
  const commercialBasePlans = useMemo(
    () => sortPlansByInterval(filterCommercialBasicPlans(plans)),
    [plans],
  );
  const commercialPremiumPlans = useMemo(
    () => sortPlansByInterval(filterCommercialPremiumPlans(plans, premiumPriceIds)),
    [plans, premiumPriceIds],
  );
  const choiceBasePlans = commercialBasePlans.length ? commercialBasePlans : basePlans;
  const choicePremiumPlans = commercialPremiumPlans.length ? commercialPremiumPlans : premiumPlans;

  const baseHighlights = useMemo(() => getTierCardHighlights(false), []);
  const premiumHighlights = useMemo(() => getTierCardHighlights(true), []);
  const currentHighlights = useMemo(
    () => getTierCardHighlights(Boolean(currentPlan?.isPremium)),
    [currentPlan?.isPremium],
  );
  const comparisonRows = useMemo(() => buildTierComparisonRows(), []);

  const currentTierPlans = currentPlan?.isPremium ? premiumPlans : basePlans;
  const hasUpgradeTier =
    isSubscribed &&
    !needsPlanChoice &&
    currentPlan &&
    !currentPlan.isPremium &&
    choicePremiumPlans.length > 0;
  // Con listino a più frequenze si può cambiare cadenza anche in planMode=custom
  // (es. price custom 50€ Premium mensile + trimestrale/annuale di catalogo).
  const canSwitchInterval = Boolean(
    currentPriceId &&
      currentTierPlans.length > 1 &&
      currentTierPlans.some((plan) => plan.id === currentPriceId),
  );
  const isFullyCustomPlan = isCustomPlan && !canSwitchInterval;
  const showDualChoice =
    needsPlanChoice && choiceBasePlans.length > 0 && choicePremiumPlans.length > 0;
  const showComparison =
    showDualChoice ||
    (!isSubscribed && choiceBasePlans.length > 0 && choicePremiumPlans.length > 0) ||
    Boolean(hasUpgradeTier);
  // Piani chiusi di default se l'accesso è valido (abbonato stabile).
  // Aperti quando deve scegliere: setup, scaduto, o limbo prova Premium.
  const canTogglePlans =
    !billingSetupRequired && (Boolean(isSubscribed) || hasAccessDays) && !needsPlanChoice;
  const showPlans = needsPlanChoice || !canTogglePlans || plansExpanded;
  const showUpgradeCta =
    canTogglePlans && Boolean(isSubscribed) && hasUpgradeTier && !actionsDisabled && !isFullyCustomPlan;
  const toggleLabel = plansExpanded
    ? t('settings.payments.planCompare.hidePlans')
    : !isSubscribed
      ? t('settings.payments.planCompare.choosePlan')
      : isFullyCustomPlan
        ? t('settings.payments.planCompare.planDetails')
        : t('settings.payments.planCompare.changePlan');

  useEffect(() => {
    if (needsPlanChoice) setPlansExpanded(true);
  }, [needsPlanChoice]);

  const openPlans = () => {
    setPlansExpanded(true);
    requestAnimationFrame(() => {
      plansWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const currentTierIntervalOptions = useMemo(
    () => (
      canSwitchInterval
        ? buildIntervalOptions(currentTierPlans, { currentPriceId })
        : []
    ),
    [canSwitchInterval, currentTierPlans, currentPriceId],
  );
  const premiumIntervalOptions = useMemo(
    () => buildIntervalOptions(choicePremiumPlans, { currentPriceId: needsPlanChoice ? null : currentPriceId }),
    [choicePremiumPlans, currentPriceId, needsPlanChoice],
  );
  const baseIntervalOptions = useMemo(
    () => buildIntervalOptions(choiceBasePlans, { currentPriceId: needsPlanChoice ? null : currentPriceId }),
    [choiceBasePlans, currentPriceId, needsPlanChoice],
  );

  const defaultCurrentIntervalType = useMemo(
    () =>
      currentPlan
        ? resolveSelectableIntervalType(currentPlan.intervalType, currentTierPlans)
        : null,
    [currentPlan?.intervalType, currentTierPlans],
  );

  const defaultUpgradeIntervalType = useMemo(
    () =>
      hasUpgradeTier && currentPlan
        ? resolveSelectableIntervalType(currentPlan.intervalType, choicePremiumPlans)
        : null,
    [hasUpgradeTier, currentPlan?.intervalType, choicePremiumPlans],
  );

  const defaultNewUserStandardInterval = useMemo(
    () => baseIntervalOptions[0]?.intervalType ?? null,
    [baseIntervalOptions],
  );

  const defaultNewUserPremiumInterval = useMemo(
    () => premiumIntervalOptions[0]?.intervalType ?? null,
    [premiumIntervalOptions],
  );

  const requestPlanChange = useCallback((rawPlan) => {
    if (!rawPlan || billingSetupRequired || !clientId) return;
    if (isFullyCustomPlan) return;
    const plan = parsePlanDisplay(rawPlan);
    if (!isPlanSelectable(currentPriceId, plan.id)) return;
    setConfirmPlan(plan);
  }, [currentPriceId, isFullyCustomPlan, billingSetupRequired, clientId]);

  useEffect(() => {
    if (!confirmPlan?.id || !clientId) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    paymentsApi
      .post('/previewSubscriptionChange', { clientId, priceId: confirmPlan.id })
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError('Impossibile calcolare il conguaglio. Puoi comunque confermare la modifica.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [confirmPlan, clientId]);

  const getStatusBadge = () => {
    if (sepaProcessing) {
      return {
        label: t('settings.payments.subscription.sepaProcessing.statusBadge'),
        className: styles.status_badge_processing,
      };
    }
    if (unpaidInvoice) {
      return { label: 'Pagamento in sospeso', className: styles.status_badge_warning };
    }
    if (isSubscribed && products?.giorniRimanenti > 3) {
      return { label: 'Abbonamento attivo', className: styles.status_badge_active };
    }
    if (isSubscribed && products?.giorniRimanenti <= 3) {
      return { label: 'Abbonamento in scadenza', className: styles.status_badge_warning };
    }
    if (!isSubscribed) {
      return { label: 'Nessun abbonamento attivo', className: styles.status_badge_inactive };
    }
    return { label: 'Abbonamento scaduto', className: styles.status_badge_inactive };
  };

  const statusBadge = getStatusBadge();

  const handleConfirm = () => {
    if (!confirmPlan) return;
    onSelectPlan(confirmPlan.id);
    setConfirmPlan(null);
  };

  const getConfirmMessage = (plan) => {
    if (collectPaymentOnConfirm && !currentPlan) {
      return `Si apre Stripe per inserire carta o addebito RID e attivare il piano ${getTierLabel(plan.isPremium)} ${plan.intervalLabel}.`;
    }
    if (currentRawPlan && isUpgradePlan(currentRawPlan, findPlanByPriceId(plans, plan.id))) {
      return `Stai passando al piano ${getTierLabel(plan.isPremium)} ${plan.intervalLabel}.`;
    }
    if (currentPlan) {
      return `Stai cambiando la mensilità in ${plan.intervalLabel}.`;
    }
    return `Stai attivando il piano ${getTierLabel(plan.isPremium)} ${plan.intervalLabel}.`;
  };

  const getConfirmTitle = (plan) => {
    if (!plan) return 'Conferma';
    if (collectPaymentOnConfirm && !currentPlan) return 'Pagamento del piano';
    if (currentRawPlan && isUpgradePlan(currentRawPlan, findPlanByPriceId(plans, plan.id))) {
      return 'Conferma upgrade';
    }
    if (currentPlan) return 'Conferma cambio piano';
    return 'Conferma attivazione';
  };

  const getConfirmCta = (plan) => {
    if (!plan) return 'Conferma';
    if (collectPaymentOnConfirm && !currentPlan) return 'Vai al pagamento';
    if (currentRawPlan && isUpgradePlan(currentRawPlan, findPlanByPriceId(plans, plan.id))) {
      return 'Conferma upgrade';
    }
    if (currentPlan) return 'Conferma modifica';
    return plan.isPremium ? 'Attiva Premium' : 'Attiva Basic';
  };

  const renderPreviewBox = () => {
    if (previewLoading) {
      return (
        <div className={styles.preview_box}>
          <CircularProgress size={22} />
          <span>Calcolo importo...</span>
        </div>
      );
    }
    if (!preview) return null;
    const recurringPrice = preview.recurringMonthlyPrice ?? confirmPlan?.price ?? null;
    return (
      <div
        className={clsx(styles.preview_box, {
          [styles.preview_box_premium]: confirmPlan?.isPremium,
        })}
      >
        <p className={styles.preview_box_label}>Riepilogo importi</p>
        {preview.hasImmediateCharge ? (
          <p className={styles.preview_row}>
            <span>Addebito oggi</span>
            <strong>{formatEuroAmount(preview.amountDue, { fromCents: true })} € + IVA</strong>
          </p>
        ) : (
          <p className={styles.preview_note}>Nessun addebito immediato previsto.</p>
        )}
        {recurringPrice != null && (
          <p className={styles.preview_row}>
            <span>Dal prossimo rinnovo</span>
            <strong>{formatEuroAmount(recurringPrice)} € + IVA / mese</strong>
          </p>
        )}
        {preview.isUpgrade && preview.hasImmediateCharge && (
          <p className={styles.preview_hint}>Include il conguaglio per il periodo rimanente.</p>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <CircularProgress size={32} />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className={styles.plans_section}>
        <div className={styles.card}>
          <div className={styles.card_header}>
            <span className={styles.card_icon} aria-hidden="true">
              <CreditCard size={18} strokeWidth={2.2} />
            </span>
            <div className={styles.card_header_copy}>
              {billingSetupRequired ? (
                <>
                  <span className={styles.step_badge}>{t('settings.payments.plansPreview.step')}</span>
                  <h4 className={styles.section_title}>{t('settings.payments.plansPreview.title')}</h4>
                  <p className={styles.section_description}>{t('settings.payments.plansPreview.lockedHint')}</p>
                </>
              ) : (
                <>
                  <h4 className={styles.section_title}>Gestione abbonamento</h4>
                  <span className={clsx(styles.status_badge, styles.status_badge_inactive)}>
                    {t('settings.payments.plansError.badge')}
                  </span>
                  <p className={styles.section_description}>
                    {t('settings.payments.plansError.message')}
                  </p>
                  {process.env.NODE_ENV === 'development' ? (
                    <p className={styles.section_description}>
                      <small>
                        {errorMessage ||
                          (!products?.prezzi?.length
                            ? `[dev] Nessun prezzo da ${process.env.REACT_APP_API_URL}; payment ms: ${process.env.REACT_APP_PAYMENT_BE_ENV}`
                            : '[dev] Nessun piano da Stripe: verifica il microservizio payment (IS_DEV=false per live).')}
                      </small>
                    </p>
                  ) : null}
                  {onRetry && (
                    <button type="button" className={styles.retry_button} onClick={onRetry}>
                      {t('communications.retry')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.plans_section}>
      <div className={styles.card}>
      <div className={clsx(styles.card_header, { [styles.card_header_collapsed]: !showPlans })}>
        <span className={styles.card_icon} aria-hidden="true">
          <CreditCard size={18} strokeWidth={2.2} />
        </span>
        <div className={styles.card_header_copy}>
        {billingSetupRequired ? (
          <>
            <span className={styles.step_badge}>{t('settings.payments.plansPreview.step')}</span>
            <h4 className={styles.section_title}>{t('settings.payments.plansPreview.title')}</h4>
            <p className={styles.section_description}>{t('settings.payments.plansPreview.lockedHint')}</p>
          </>
        ) : sepaProcessing ? (
          <>
            <h4 className={styles.section_title}>
              {t('settings.payments.subscription.sepaProcessing.plansTitle')}
            </h4>
            <span className={clsx(styles.status_badge, styles.status_badge_processing)}>
              {t('settings.payments.subscription.sepaProcessing.statusBadge')}
            </span>
            <p className={styles.section_description}>
              {t('settings.payments.subscription.sepaProcessing.plansHint')}
            </p>
            {isSubscribed && currentPlan && (
              <p className={styles.plans_current_summary}>
                <strong>
                  {getPlanTierLabel(currentPlan, {
                    isCustomPlan,
                    isPremium: currentPlan.isPremium,
                    allowCatalogLabel: canSwitchInterval,
                  })}
                  {!isFullyCustomPlan && ` · ${currentPlan.intervalLabel}`}
                </strong>
                {' — '}
                {formatEuroAmount(currentPlan.price)}€ + IVA / mese
              </p>
            )}
          </>
        ) : unpaidInvoice ? (
          <>
            <h4 className={styles.section_title}>
              {t('settings.payments.subscription.unpaidBanner.plansLockedTitle')}
            </h4>
            <span className={clsx(styles.status_badge, styles.status_badge_warning)}>
              {t('settings.payments.subscription.unpaidBanner.badge')}
            </span>
            <p className={styles.section_description}>
              {t('settings.payments.subscription.unpaidBanner.plansLockedHint')}
            </p>
            {onFocusPayment ? (
              <button type="button" className={styles.unlock_payment_link} onClick={onFocusPayment}>
                {t('settings.payments.subscription.unpaidBanner.plansLockedCta')}
              </button>
            ) : null}
          </>
        ) : (
          <>
            <h4 className={styles.section_title}>Gestione abbonamento</h4>
            <span className={clsx(styles.status_badge, statusBadge.className)}>{statusBadge.label}</span>
            {subscriptionStatus?.currentPeriodEnd && isSubscribed && (
              <p className={styles.section_description}>
                Prossimo rinnovo: {dayjs(subscriptionStatus.currentPeriodEnd).format('DD/MM/YYYY')}
                {subscriptionStatus.cancelAtPeriodEnd && ' (disdetta programmata)'}
              </p>
            )}
            {isSubscribed && currentPlan && (
              <p className={styles.plans_current_summary}>
                <strong>
                  {getPlanTierLabel(currentPlan, {
                    isCustomPlan,
                    isPremium: currentPlan.isPremium,
                    allowCatalogLabel: canSwitchInterval,
                  })}
                  {!isFullyCustomPlan && ` · ${currentPlan.intervalLabel}`}
                </strong>
                {' — '}
                {formatEuroAmount(currentPlan.price)}€ + IVA / mese
              </p>
            )}
            {isSubscribed && !currentPlan && products?.subscriptionFrequency?.label ? (
              <p className={styles.plans_current_summary}>
                <strong>
                  {isFullyCustomPlan ? 'Custom' : 'Basic'}
                  {` · ${products.subscriptionFrequency.label}`}
                </strong>
              </p>
            ) : null}
            {products?.scadenza && !subscriptionStatus?.currentPeriodEnd && (
              <p className={styles.section_description}>Scadenza: {dayjs(products.scadenza).format('DD/MM/YYYY')}</p>
            )}
          </>
        )}
        </div>

        {canTogglePlans ? (
          <div className={styles.card_header_actions}>
            {showUpgradeCta ? (
              <button
                type="button"
                className={clsx(styles.header_cta, styles.header_cta_premium)}
                onClick={openPlans}
              >
                <Flame size={15} strokeWidth={2.4} aria-hidden />
                {t('settings.payments.planCompare.upgradePremium')}
              </button>
            ) : null}
            <button
              type="button"
              className={clsx(styles.header_cta, {
                [styles.header_cta_primary]: !isSubscribed && !plansExpanded,
              })}
              aria-expanded={plansExpanded}
              aria-controls="payments-plans-wrap"
              onClick={() => (plansExpanded ? setPlansExpanded(false) : openPlans())}
            >
              {toggleLabel}
              <ChevronDown
                size={15}
                strokeWidth={2.4}
                aria-hidden
                className={clsx(styles.header_cta_chevron, {
                  [styles.header_cta_chevron_open]: plansExpanded,
                })}
              />
            </button>
          </div>
        ) : null}
      </div>

      {showPlans ? (
      <div
        id="payments-plans-wrap"
        ref={plansWrapRef}
        className={clsx(styles.plans_cards_wrap, {
          [styles.plans_cards_wrap_single]: isSubscribed && !hasUpgradeTier && !showDualChoice,
          [styles.plans_cards_wrap_locked]: billingSetupRequired || unpaidInvoice,
        })}
        aria-hidden={unpaidInvoice ? true : undefined}
      >
      {isSubscribed && !showDualChoice ? (
        <div
          className={clsx(styles.plans_layout, {
            [styles.plans_layout_single]: !hasUpgradeTier,
          })}
        >
          <PlanTierCard
            badge={t('settings.payments.planCompare.yourPlan')}
            tierLabel={getPlanTierLabel(currentPlan, {
              isCustomPlan,
              isPremium: currentPlan?.isPremium,
              allowCatalogLabel: canSwitchInterval,
            })}
            pitch={
              currentPlan?.isPremium
                ? t('settings.payments.planCompare.pitchPremium')
                : t('settings.payments.planCompare.pitchBasic')
            }
            plan={currentPlan}
            featureRows={currentHighlights}
            featuresTitle={t('settings.payments.planCompare.basicEssentialsTitle')}
            extrasTitle={
              currentPlan?.isPremium
                ? t('settings.payments.planCompare.premiumExtrasTitle')
                : undefined
            }
            upgradeTagLabel={
              currentPlan?.isPremium
                ? t('settings.payments.planCompare.upgradeTag')
                : undefined
            }
            additionTagLabel={
              currentPlan?.isPremium
                ? t('settings.payments.planCompare.additionTag')
                : undefined
            }
            excludedTitle={
              currentPlan?.isPremium
                ? undefined
                : t('settings.payments.planCompare.basicMissingTitle')
            }
            isPremium={currentPlan?.isPremium}
            isCurrent
            isCustomPlan={isFullyCustomPlan}
            intervalOptions={currentTierIntervalOptions}
            defaultIntervalType={defaultCurrentIntervalType}
            currentPriceId={currentPriceId}
            disabled={actionsDisabled || isFullyCustomPlan}
            onPlanAction={requestPlanChange}
            billingLabel={t('settings.payments.planCompare.chooseBilling')}
            activeTagLabel={t('settings.payments.planCompare.activeTag')}
            currentPlanCtaLabel={t('settings.payments.planCompare.currentPlanCta')}
            confirmIntervalLabel={(interval) =>
              t('settings.payments.planCompare.confirmInterval', { interval })
            }
          />

          {hasUpgradeTier && (
            <PlanTierCard
              badge={t('settings.payments.planCompare.upgradeAvailable')}
              tierLabel={t('settings.payments.planCompare.tierPremium')}
              pitch={t('settings.payments.planCompare.pitchPremium')}
              featureRows={premiumHighlights}
              featuresTitle={t('settings.payments.planCompare.basicEssentialsTitle')}
              extrasTitle={t('settings.payments.planCompare.premiumExtrasTitle')}
              upgradeTagLabel={t('settings.payments.planCompare.upgradeTag')}
              additionTagLabel={t('settings.payments.planCompare.additionTag')}
              isPremium
              isFeatured
              isSecondary
              intervalOptions={premiumIntervalOptions}
              defaultIntervalType={defaultUpgradeIntervalType}
              disabled={actionsDisabled}
              isSubmitting={isSubmitting}
              ctaLabel={t('settings.payments.planCompare.upgradePremium')}
              onPlanAction={requestPlanChange}
              billingLabel={t('settings.payments.planCompare.chooseBilling')}
              activeTagLabel={t('settings.payments.planCompare.activeTag')}
              currentPlanCtaLabel={t('settings.payments.planCompare.currentPlanCta')}
            />
          )}
        </div>
      ) : (
        <div className={clsx(styles.plans_layout, styles.plans_layout_acquisition)}>
          <PlanTierCard
            tierLabel={t('settings.payments.planCompare.tierBasic')}
            pitch={t('settings.payments.planCompare.pitchBasic')}
            featureRows={baseHighlights}
            featuresTitle={t('settings.payments.planCompare.basicEssentialsTitle')}
            excludedTitle={t('settings.payments.planCompare.basicMissingTitle')}
            intervalOptions={baseIntervalOptions}
            defaultIntervalType={defaultNewUserStandardInterval}
            disabled={actionsDisabled}
            isSecondary
            ctaLabel={t('settings.payments.planCompare.activateBasic')}
            onPlanAction={requestPlanChange}
            billingLabel={t('settings.payments.planCompare.chooseBilling')}
            activeTagLabel={t('settings.payments.planCompare.activeTag')}
            currentPlanCtaLabel={t('settings.payments.planCompare.currentPlanCta')}
          />
          <PlanTierCard
            badge={t('settings.payments.planCompare.recommended')}
            tierLabel={t('settings.payments.planCompare.tierPremium')}
            pitch={t('settings.payments.planCompare.pitchPremium')}
            featureRows={premiumHighlights}
            featuresTitle={t('settings.payments.planCompare.basicEssentialsTitle')}
            extrasTitle={t('settings.payments.planCompare.premiumExtrasTitle')}
            upgradeTagLabel={t('settings.payments.planCompare.upgradeTag')}
            additionTagLabel={t('settings.payments.planCompare.additionTag')}
            isPremium
            isFeatured
            intervalOptions={premiumIntervalOptions}
            defaultIntervalType={defaultNewUserPremiumInterval}
            disabled={actionsDisabled}
            isSubmitting={isSubmitting}
            ctaLabel={t('settings.payments.planCompare.activatePremium')}
            onPlanAction={requestPlanChange}
            billingLabel={t('settings.payments.planCompare.chooseBilling')}
            activeTagLabel={t('settings.payments.planCompare.activeTag')}
            currentPlanCtaLabel={t('settings.payments.planCompare.currentPlanCta')}
          />
        </div>
      )}

      {showComparison ? (
        <div className={styles.compare_table_wrap}>
          <h5 className={styles.compare_table_title}>
            {t('settings.payments.planCompare.featuresComparison')}
          </h5>
          <div className={styles.compare_table_scroll}>
            <table className={styles.compare_table}>
              <thead>
                <tr>
                  <th scope="col">{t('settings.payments.planCompare.featureColumn')}</th>
                  <th scope="col">{t('settings.payments.planCompare.tierBasic')}</th>
                  <th scope="col">{t('settings.payments.planCompare.tierPremium')}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td>
                      <span
                        className={clsx(styles.compare_cell, {
                          [styles.compare_cell_yes]: row.basic,
                          [styles.compare_cell_no]: !row.basic,
                        })}
                        aria-label={
                          row.basic
                            ? t('settings.payments.planCompare.included')
                            : t('settings.payments.planCompare.notIncluded')
                        }
                      >
                        {row.basic ? <Check size={16} strokeWidth={2.6} /> : <X size={14} strokeWidth={2.8} />}
                      </span>
                    </td>
                    <td>
                      <span
                        className={clsx(styles.compare_cell, {
                          [styles.compare_cell_yes]: row.premium,
                          [styles.compare_cell_no]: !row.premium,
                        })}
                        aria-label={
                          row.premium
                            ? t('settings.payments.planCompare.included')
                            : t('settings.payments.planCompare.notIncluded')
                        }
                      >
                        {row.premium ? <Check size={16} strokeWidth={2.6} /> : <X size={14} strokeWidth={2.8} />}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className={styles.compare_table_actions}>
                  <th scope="row" />
                  <td>
                    {hasUpgradeTier ? (
                      <button
                        type="button"
                        className={styles.compare_cta}
                        disabled
                      >
                        {t('settings.payments.planCompare.currentPlanCta')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.compare_cta}
                        disabled={actionsDisabled || !baseIntervalOptions[0]?.rawPlan}
                        onClick={() => requestPlanChange(baseIntervalOptions[0]?.rawPlan)}
                      >
                        {t('settings.payments.planCompare.activateBasic')}
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={clsx(styles.compare_cta, styles.compare_cta_premium)}
                      disabled={
                        actionsDisabled
                        || isSubmitting
                        || !(
                          hasUpgradeTier
                            ? premiumIntervalOptions.find(
                              (option) => option.intervalType === defaultUpgradeIntervalType,
                            )?.rawPlan || premiumIntervalOptions[0]?.rawPlan
                            : premiumIntervalOptions[0]?.rawPlan
                        )
                      }
                      onClick={() => {
                        const premiumPlan = hasUpgradeTier
                          ? (
                            premiumIntervalOptions.find(
                              (option) => option.intervalType === defaultUpgradeIntervalType,
                            )?.rawPlan || premiumIntervalOptions[0]?.rawPlan
                          )
                          : premiumIntervalOptions[0]?.rawPlan;
                        requestPlanChange(premiumPlan);
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        t(
                          hasUpgradeTier
                            ? 'settings.payments.planCompare.upgradePremium'
                            : 'settings.payments.planCompare.activatePremium',
                        )
                      )}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      </div>
      ) : null}

      {errorMessage && <p className={styles.error_message}>{errorMessage}</p>}
      </div>

      <Dialog
        open={!!confirmPlan}
        onClose={() => setConfirmPlan(null)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: styles.confirm_paper }}
      >
        <DialogTitle className={styles.confirm_title}>
          {getConfirmTitle(confirmPlan)}
        </DialogTitle>
        <DialogContent className={styles.confirm_dialog}>
          {confirmPlan ? (
            <div
              className={clsx(styles.confirm_plan_chip, {
                [styles.confirm_plan_chip_premium]: confirmPlan.isPremium,
              })}
            >
              <span className={styles.confirm_plan_chip_icon} aria-hidden>
                {confirmPlan.isPremium ? (
                  <Flame size={18} strokeWidth={2.2} fill="currentColor" />
                ) : (
                  <Layers size={18} strokeWidth={2.2} />
                )}
              </span>
              <div className={styles.confirm_plan_chip_copy}>
                <strong>
                  {getTierLabel(confirmPlan.isPremium)} · {confirmPlan.intervalLabel}
                </strong>
                <span>{getConfirmMessage(confirmPlan)}</span>
              </div>
            </div>
          ) : null}
          {renderPreviewBox()}
          {previewError && <p className={styles.preview_error}>{previewError}</p>}
        </DialogContent>
        <DialogActions className={styles.confirm_actions}>
          <button
            type="button"
            className={styles.confirm_btn_secondary}
            onClick={() => setConfirmPlan(null)}
          >
            Annulla
          </button>
          <button
            type="button"
            className={clsx(styles.confirm_btn_primary, {
              [styles.confirm_btn_primary_premium]: confirmPlan?.isPremium,
            })}
            onClick={handleConfirm}
            disabled={isSubmitting || previewLoading}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : getConfirmCta(confirmPlan)}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
